import {useInfiniteQuery} from "react-query";

import {
  SearchRequest,
  SearchResponse,
  WorkflowCard,
  WorkflowStatus,
  WorkflowsService,
} from "../../../services/WorkflowsService";

export const WORKFLOWS_QUERY_KEY = "workflows";

/** Matches the server default: the most recent N of the status, per page. */
export const DEFAULT_PAGE_SIZE = 10;

/** Shortest term the service filters on. Below it, no term is sent at all. */
export const MIN_SEARCH_TERM_LENGTH = 3;

/** Longest term the contract accepts. Anything above is a 422. */
export const MAX_SEARCH_TERM_LENGTH = 100;

export const normalizeSearchTerm = (query?: string | null) => {
  const trimmed = query?.trim();
  return trimmed && trimmed.length >= MIN_SEARCH_TERM_LENGTH ? trimmed : undefined;
};

type Params = {
  /** Omit for every status — list view's "All", and its default. */
  status?: WorkflowStatus | null;
  facilityIds: ReadonlyArray<string> | null;
  /** Narrows to the caller's stars. Resolved in `reside`, not the replica. */
  starredOnly?: boolean;
  /** Dashboard search term, matched against resident name and workflow code. */
  query?: string | null;
  workflowType?: string | null;
  pageSize?: number;
  sortBy?: SearchRequest["sortBy"];
  sortDirection?: SearchRequest["sortDirection"];
  /** A newly created user without a facility is not allowed to see workflows. */
  isAllowedToSeeAdmissions?: boolean;
};

const toRequest = (
  {
    status,
    facilityIds,
    query,
    workflowType,
    starredOnly,
    pageSize = DEFAULT_PAGE_SIZE,
    sortBy,
    sortDirection,
  }: Params,
  page: number,
): SearchRequest => ({
  // Omitted when absent: to the service, no statuses means every status.
  ...(status ? {statuses: [status]} : {}),
  // Omitted rather than sent empty: the server treats absent as "everything in scope", while
  // an empty list would be a filter matching nothing.
  ...(facilityIds?.length ? {facilityIds: [...facilityIds]} : {}),
  ...(query ? {query} : {}),
  ...(workflowType ? {workflowType} : {}),
  // Always sent, like pageSize: the contract gives it a default, which makes it a required
  // field of the generated request type rather than an optional one.
  starredOnly: Boolean(starredOnly),
  ...(sortBy ? {sortBy} : {}),
  ...(sortDirection ? {sortDirection} : {}),
  page,
  pageSize,
});

/**
 * Patches one card (by id) inside every page of a cached workflows result.
 *
 * The board writes through Java and reads from the replica, so a mutation's response cannot
 * simply be refetched: the replica is a second or two behind, and a refetch inside that window
 * returns the row as it was and visibly undoes what the user just did. Patching the cache is
 * what keeps a checklist tick or a PCC upload on screen until replication catches up.
 *
 * Only the keys the card already carries are taken from the response, and `answers` is left
 * alone: the service narrows it to a preview subset deliberately, and overlaying the full set
 * from the Java DTO would put tens of kilobytes of unread answers into every patched card.
 *
 * Returns the original reference when the card is not in this query, so unrelated columns do
 * not re-render.
 */
export const replaceCardInWorkflowsData = (
  data: {pages: SearchResponse[]} | undefined,
  admission: {id: string} & Record<string, unknown>,
): {pages: SearchResponse[]} | undefined => {
  if (!data?.pages?.length) {
    return data;
  }

  const merge = (card: WorkflowCard): WorkflowCard => {
    const patched: Record<string, unknown> = {...card};
    for (const key of Object.keys(card)) {
      if (key === "answers" || !(key in admission)) {
        continue;
      }
      patched[key] = admission[key];
    }
    // Cards carry the count, Java returns the flags themselves; same rule as getFlagCount.
    if ("flagsCount" in card && Array.isArray(admission.flags)) {
      patched.flagsCount = admission.flags.length;
    }
    return patched as WorkflowCard;
  };

  let changed = false;
  const pages = data.pages.map(page => {
    if (!page?.cards?.some(card => card.id === admission.id)) {
      return page;
    }
    changed = true;
    return {
      ...page,
      cards: page.cards.map(card => (card.id === admission.id ? merge(card) : card)),
    };
  });

  return changed ? {...data, pages} : data;
};

/** How long after a write the replica is expected to have it: a transform poll (5s), with room. */
export const REPLICA_CATCH_UP_MS = 8_000;

const unwrap = (response: {ok: boolean; data?: SearchResponse}): SearchResponse => {
  if (!response.ok || !response.data) {
    // Thrown so react-query surfaces it. The apisauce layer has already handled 401/403 by
    // logging out, so anything reaching here is a real failure rather than an expired session.
    throw response;
  }
  return response.data;
};

/**
 * One page of workflows, extended by Load More.
 *
 * An infinite query per column, matching the Java hook it replaces. `refetchQueries` re-fetches
 * every loaded page of one, so a column expanded by Load More stays coherent after an approve
 * or archive — appending to a shared first page would leave the later ones stale.
 */
export const useWorkflowsQuery = (params: Params) => {
  const facilitiesLoaded = params.facilityIds !== null;
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  // Normalised before the query key, so "jo" and "" are one cached query.
  const query = normalizeSearchTerm(params.query);

  const result = useInfiniteQuery<SearchResponse>(
    [
      WORKFLOWS_QUERY_KEY,
      {
        status: params.status,
        facilityIds: params.facilityIds,
        query,
        workflowType: params.workflowType,
        starredOnly: params.starredOnly,
        sortBy: params.sortBy,
        sortDirection: params.sortDirection,
        pageSize,
      },
    ],
    async ({pageParam = 0}) =>
      unwrap(await WorkflowsService.search(toRequest({...params, query}, pageParam))),
    {
      // From the response, not `total` — that saturates at the service's cap.
      getNextPageParam: ({hasMore}, allPages) => (hasMore ? allPages.length : false),
      enabled:
        facilitiesLoaded &&
        (params.isAllowedToSeeAdmissions === undefined ||
          params.isAllowedToSeeAdmissions),
      /*
       * Off, as it was on the Java hook this replaces, and for a reason that is specific to
       * reading a replica: a checklist tick or a PCC upload lives in this cache because the
       * replica lags the Java write that produced it. A mount-time refetch -- toggling tile and
       * list remounts the column -- would fetch the row as the replica still has it and put the
       * pre-tick card back on screen. The cache is the newer copy here, not the stale one.
       *
       * Star refetches this key on its own timer. A status change polls through
       * tryRefreshDashboard and refetches once search shows the new status.
       */
      refetchOnMount: false,
      /*
       * StarFilter disables itself when this is falsy, and nothing else writes it now that the
       * board and the list both read this service. Without it the control is permanently greyed
       * out, whatever the user has starred.
       *
       * The caller's whole count, not this column's: four columns each report the same figure,
       * so the last to land does not shrink it to its own share.
       */
      onSuccess: data => {
        const total = data.pages[data.pages.length - 1]?.totalStarred;
        if (total === undefined) {
          return;
        }
        sessionStorage.setItem("totalStarredAdmissions", total.toString());
        window.dispatchEvent(new Event("starredAdmissionsUpdated"));
      },
    },
  );

  /*
   * The term the request actually carried, not the one that was typed. Callers rendering "no
   * matches for X" must use this: below MIN_SEARCH_TERM_LENGTH no term is sent, so the raw
   * prop would blame a search that never ran.
   */
  return {...result, appliedQuery: query};
};
